import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { UniversalLink } from '@plone/volto/components';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  CardText,
} from 'design-react-kit';

import cx from 'classnames';
import { defineMessages, useIntl } from 'react-intl';
import { injectLazyLibs } from '@plone/volto/helpers/Loadable/Loadable';
import { viewDate } from 'design-comuni-plone-theme/helpers';
import {
  getCalendarDate,
  getEventRecurrenceMore,
} from 'design-comuni-plone-theme/helpers';
import {
  ListingText,
  ListingLinkMore,
  ListingImage,
} from 'design-comuni-plone-theme/components/ItaliaTheme';

const messages = defineMessages({
  fonte: {
    id: 'fonte',
    defaultMessage: 'Fonte',
  },
  publication_author: {
    id: 'list_publication_author',
    defaultMessage: 'Autore',
  },
  publication_date: {
    id: 'list_publication_date',
    defaultMessage: 'Data',
  },
  publication_type: {
    id: 'list_publication_type',
    defaultMessage: 'Tipo',
  },
});

const ListTemplate = (props) => {
  const intl = useIntl();
  moment.locale(intl.locale);

  const {
    items,
    isEditMode,
    linkTitle,
    linkHref,
    linkAlign,
    titleLine,
    title,
    show_block_bg,
    id_lighthouse,
    linkmore_id_lighthouse,
    rrule,
  } = props;

  return (
    <Container className="px-4">
      <div className="list-template">
        {title && (
          <Row className={'template-header'}>
            <Col md={12}>
              <h2
                className={cx('mb-2', {
                  'title-bottom-line': titleLine,
                  'mt-5': !show_block_bg,
                })}
              >
                {title}
              </h2>
            </Col>
          </Row>
        )}

        <div className="items-wrapper mb-3">
          {items.map((item, index) => {
            const type = item['@type'];
            const itemTitle = item.title || item.id;
            const date =
              getCalendarDate(item, rrule.rrulestr) ??
              moment(item.Date).format('DD MMM YYYY');
            const eventRecurrenceMore = getEventRecurrenceMore(
              item,
              isEditMode,
            );

            const listingText = <ListingText item={item} />;
            const image = ListingImage({ item });
            let itemEnhanced = { ...item };
            if (type === 'Pubblicazione') {
              itemEnhanced.enhanced_links_enabled = false; //no enhanced link for items of type 'Pubblicazione'
            }

            return (
              <Card key={index} className={`shadow no-after my-md-3 ${type}`}>
                <CardBody>
                  <CardTitle
                    tag={title ? 'h3' : 'h2'}
                    className={cx('', {
                      'line-height-title': !title,
                    })}
                  >
                    <UniversalLink
                      item={!isEditMode ? itemEnhanced : null}
                      href={isEditMode ? '#' : null}
                      data-element={id_lighthouse}
                    >
                      {itemTitle}
                    </UniversalLink>
                  </CardTitle>
                  <CardText>
                    {image && props.show_image && (
                      <div className="card-image me-3">
                        <figure className="img-wrapper">{image}</figure>
                      </div>
                    )}
                    <div className="card-content">
                      {type === 'Pubblicazione' ? (
                        <div className="text publication">
                          {props.show_description && <>{listingText}</>}
                          {(item.authors?.length > 0 || item.Date) && (
                            <div className="publication-row-infos">
                              {item.authors?.length > 0 && (
                                <div className="pubblication_info">
                                  <span className="publication_label">
                                    {intl.formatMessage(
                                      messages.publication_author,
                                    )}
                                    :{' '}
                                  </span>
                                  <span className="publication_value">
                                    {item.authors.map((author, index) => (
                                      <>
                                        <UniversalLink
                                          href={`/search?authors=${author}`}
                                        >
                                          {author}
                                        </UniversalLink>
                                        {index < item.authors.length - 1 && (
                                          <>, </>
                                        )}
                                      </>
                                    ))}
                                  </span>
                                </div>
                              )}
                              {item.publication_date && (
                                <div className="pubblication_info">
                                  <span className="publication_label">
                                    {intl.formatMessage(
                                      messages.publication_date,
                                    )}
                                    :{' '}
                                  </span>
                                  <span className="publication_value">
                                    {viewDate(
                                      intl.locale,
                                      item.publication_date,
                                      'DD/MM/yyyy',
                                    )}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {item.publicationType?.length > 0 && (
                            <div className="publication-row-infos">
                              <div className="pubblication_info">
                                <span className="publication_label">
                                  {intl.formatMessage(
                                    messages.publication_type,
                                  )}
                                  :{' '}
                                </span>
                                <span className="publication_value">
                                  {item.publicationType.map((type, index) => (
                                    <>
                                      {type.title}
                                      {index <
                                        item.publicationType.length - 1 && (
                                        <>, </>
                                      )}
                                    </>
                                  ))}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          {(props.show_description || !props.hide_dates) && (
                            <div className="text">
                              {props.show_description && <>{listingText}</>}
                              {!props.hide_dates && (
                                <div className="date mt-1">
                                  {(eventRecurrenceMore || date) && (
                                    <>
                                      {date ? date : <></>}
                                      {eventRecurrenceMore}
                                    </>
                                  )}
                                  {item.externalSource && (
                                    <>
                                      {' '}
                                      - {intl.formatMessage(
                                        messages.fonte,
                                      )}:{' '}
                                      <span className="external-source">
                                        ({item.externalSource})
                                      </span>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </CardText>
                </CardBody>
              </Card>
            );
          })}
        </div>

        <ListingLinkMore
          title={linkTitle}
          href={linkHref}
          className="my-4"
          linkAlign={linkAlign}
          linkmoreIdLighthouse={linkmore_id_lighthouse}
        />
      </div>
    </Container>
  );
};

ListTemplate.propTypes = {
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
  isEditMode: PropTypes.bool,
  linkTitle: PropTypes.any,
  linkHref: PropTypes.any,
};

export default injectLazyLibs(['rrule'])(ListTemplate);
